import { useCallback } from "react"
import { useMutation } from "urql"

import { DeleteCompanyProductAlternativeDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseDeleteAlternativeReturn {
  deleteAlternative: (id: string) => Promise<boolean>
  isDeleting: boolean
}

export const useDeleteAlternative = (): UseDeleteAlternativeReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation(DeleteCompanyProductAlternativeDocument)

  const deleteAlternative = useCallback(
    async (id: string): Promise<boolean> => {
      const { data, error } = await executeMutation({ id })
      const output = data?.companyProductAssociation?.deleteAlternative

      if (error || output?.__typename === "CompanyProductAlternativeAssociationDeleteFailure") {
        const message =
          output?.__typename === "CompanyProductAlternativeAssociationDeleteFailure"
            ? output.error.message
            : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to remove alternative. Please try again.",
        })
        return false
      }

      notify({ type: NotificationType.Success, message: "Alternative removed" })
      return true
    },
    [executeMutation, notify]
  )

  return { deleteAlternative, isDeleting: fetching }
}
